import { Link, useSearchParams } from 'react-router-dom';
import { Sparkles, Rocket, Check, Calendar, Shield, Zap } from 'lucide-react';

const PLAN_DETAILS: Record<string, { name: string; color: string; features: string[] }> = {
  trial: {
    name: 'Free Trial',
    color: 'text-zinc-400',
    features: ['3 clients', '50 AI queries', 'Document upload & chat', 'PDF & CSV export', '1 month free access'],
  },
  starter: {
    name: 'Starter',
    color: 'text-blue-400',
    features: ['10 clients', '500 AI queries/mo', '2 users', '200 documents', 'Xero connection', 'All export formats'],
  },
  professional: {
    name: 'Professional',
    color: 'text-indigo-400',
    features: ['50 clients', '2,000 AI queries/mo', '5 users', '10,000 documents', 'ATO compliance suite', 'Firm dashboard'],
  },
  enterprise: {
    name: 'Enterprise',
    color: 'text-purple-400',
    features: ['200 clients', '10,000 AI queries/mo', '10 users', '20,000 documents', 'Tax planning & scenarios', 'Multi-client intelligence'],
  },
  unlimited: {
    name: 'Scale',
    color: 'text-amber-400',
    features: ['500 clients', '50,000 AI queries/mo', '20 users', '50,000 documents', 'All features', 'API access', 'White-label branding'],
  },
};

export default function WelcomePage() {
  const [searchParams] = useSearchParams();
  const planName = searchParams.get('plan') || 'starter';
  const email = searchParams.get('email') || '';
  const sessionId = searchParams.get('session_id') || '';
  const plan = PLAN_DETAILS[planName] || PLAN_DETAILS.starter;

  const now = new Date();
  const startDate = now.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  const endDate = new Date(now);
  if (planName === 'trial') {
    endDate.setDate(endDate.getDate() + 14);
  } else {
    endDate.setMonth(endDate.getMonth() + 1);
  }
  const endDateStr = endDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-[#262635] text-white flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 md:px-12 h-20">
        <Link to="/" className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#7c75f0]" />
          <span className="text-base tracking-[0.15em] font-light">RONIDOC<span className="font-semibold">AI</span></span>
        </Link>
      </header>

      {/* Main */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-2xl w-full text-center">

          {/* Success icon */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-[#5048e5]/10 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-[#5048e5]/20 flex items-center justify-center">
                  <Rocket className="h-8 w-8 text-[#7c75f0]" />
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="h-4 w-4 text-green-400" />
              </div>
              <div className="absolute inset-0 rounded-full bg-[#5048e5]/5 blur-[40px] pointer-events-none" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-extralight mb-4">
            You're all set.
          </h1>
          <p className="text-lg text-zinc-400 mb-12">
            Welcome to the future of accounting intelligence.
          </p>

          {/* Subscription card */}
          <div className="border border-zinc-800/60 bg-zinc-900/20 p-8 mb-10 text-left">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#9c86fd]/60 mb-1">Your Plan</p>
                <h2 className={`text-2xl font-medium ${plan.color}`}>{plan.name}</h2>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#5048e5]/10 flex items-center justify-center">
                <Shield className="h-5 w-5 text-[#7c75f0]" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="border border-zinc-800/40 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="h-3.5 w-3.5 text-zinc-600" />
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider">Start Date</p>
                </div>
                <p className="text-sm font-medium">{startDate}</p>
              </div>
              <div className="border border-zinc-800/40 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="h-3.5 w-3.5 text-zinc-600" />
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider">{planName === 'trial' ? 'Trial Ends' : 'Next Billing'}</p>
                </div>
                <p className="text-sm font-medium">{endDateStr}</p>
              </div>
            </div>

            {email && (
              <div className="border-t border-zinc-800/40 pt-4 mb-4">
                <p className="text-xs text-zinc-600">Account: <span className="text-zinc-400">{email}</span></p>
              </div>
            )}

            <div className="border-t border-zinc-800/40 pt-4">
              <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-3">What's included</p>
              <div className="grid grid-cols-2 gap-2">
                {plan.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-[#7c75f0] flex-shrink-0" />
                    <span className="text-xs text-zinc-400">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <Link
            to="/login"
            className="inline-flex items-center gap-3 bg-[#5048e5] hover:bg-[#5f58e8] text-white text-[13px] tracking-[0.15em] uppercase px-12 py-5 rounded-full transition-all duration-300 hover:shadow-[0_0_40px_rgba(80,72,229,0.4)] group"
          >
            <Zap className="h-5 w-5 group-hover:animate-pulse" />
            Launch Mission Control
            <Rocket className="h-5 w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
          </Link>

          <p className="text-xs text-zinc-700 mt-6">
            {planName === 'trial'
              ? 'Your 1 month free trial starts now. No credit card needed.'
              : 'Your subscription is active. A receipt has been sent to your email.'}
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="px-6 md:px-12 lg:px-24 py-10 border-t border-zinc-900 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#7c75f0]" />
          <span className="text-xs tracking-[0.15em] font-light text-zinc-600">RONIDOC<span className="font-semibold">AI</span></span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/terms" className="text-[11px] text-zinc-700 hover:text-zinc-400 transition">Terms</Link>
          <Link to="/privacy-policy" className="text-[11px] text-zinc-700 hover:text-zinc-400 transition">Privacy</Link>
        </div>
        <p className="text-[11px] text-zinc-800">&copy; {new Date().getFullYear()} RoniDoc AI</p>
      </footer>
    </div>
  );
}
